/**
 * `POST /v1/auth/otp/request`, `POST /v1/auth/otp/verify`, `POST /v1/auth/refresh` e
 * `POST /v1/auth/logout` — a autenticacao do CLIENTE (CONTRACTS §8.2 bloco "Auth", API §6.1,
 * schema completo do verify em API §6.20.1).
 *
 * Tres coisas que este arquivo decide e que valem ser lidas antes do codigo:
 *
 * **1. Nenhuma resposta carrega credencial.** Nao ha `accessToken`, nao ha `sessionId`, nao ha
 * o codigo do OTP em eco. A sessao viaja **so** no cookie `__Host-session` (ADR-0009,
 * CONTRACTS §5.2), e o unico segredo que atravessa o corpo e o `csrfToken` do double-submit —
 * que e, por desenho, legivel pelo JavaScript da propria origem (SECURITY §7.3) e nao
 * autentica ninguem sozinho.
 *
 * **2. A resposta de `otp/request` e uma constante.** Ela nao muda com telefone cadastrado ou
 * nao, e nao tem campo que possa variar entre os dois casos: e um objeto com dois numeros
 * fixos por politica. AMZ-002 pede corpo, status e **latencia** iguais; corpo e status a forma
 * do schema ja garante, e a latencia e responsabilidade do caso de uso.
 *
 * **3. Logout global nao ganhou rota.** `CONTRACTS §8.2` fecha o bloco Auth em 6 linhas de
 * rota e `POST /v1/auth/logout-all` (citado em SECURITY §5.2) nao e uma delas. O escopo entra
 * como campo do corpo do proprio logout — mesma capacidade, contagem de rotas intacta. Se o
 * contrato quiser a rota separada, ela nasce sem mudar o caso de uso.
 */
import { z } from 'zod';

import { ConsentPurposeSchema } from '../enums';
import { PhoneBrE164Schema, Uuidv7Schema } from '../common';

// ---------------------------------------------------------------------------
// Cookies e cabecalhos — SECURITY §7.3, ADR-0009
// ---------------------------------------------------------------------------

/**
 * Nome do cookie de sessao. O prefixo `__Host-` nao e decoracao: ele obriga `Secure`, `Path=/`
 * e **ausencia de `Domain`**, e e o que impede um subdominio irmao comprometido de sobrescrever
 * a sessao da aplicacao (SECURITY §7.3, AMZ-003).
 */
export const SESSION_COOKIE_NAME = '__Host-session' as const;

/** Cookie do double-submit. Legivel por JS **por desenho** — e o par do header. */
export const CSRF_COOKIE_NAME = '__Host-csrf' as const;

/** Header que o cliente ecoa com o valor do cookie acima em toda mutacao (SECURITY §7.4). */
export const CSRF_HEADER_NAME = 'x-csrf-token' as const;

/**
 * Cache das rotas de auth: `no-store` absoluto (API §6.1, coluna Cache `NS`).
 *
 * `no-store` e nao `no-cache`: o segundo permite armazenar e revalidar, e uma resposta de
 * login armazenada no disco de um aparelho compartilhado e exatamente o que AMZ-009 descreve.
 */
export const AUTH_CACHE_CONTROL = 'no-store' as const;

// ---------------------------------------------------------------------------
// POST /v1/auth/otp/request
// ---------------------------------------------------------------------------

/**
 * `strict()` em todo corpo de auth. Campo desconhecido e recusado em vez de ignorado: e o que
 * transforma `{ phone, isAdmin: true }` num `422` em vez de num silencio (SEC-17, mass
 * assignment).
 */
export const RequestOtpRequestSchema = z
  .object({
    phone: PhoneBrE164Schema,
  })
  .strict();
export type RequestOtpRequest = z.infer<typeof RequestOtpRequestSchema>;

/**
 * `202 Accepted`, **sempre** — inclusive para telefone que nao existe, para telefone barrado
 * por rate limit e para canal de envio indisponivel (AMZ-002, API §6.1).
 *
 * Nao ha `challengeId` no corpo. Devolver o id do desafio daria ao atacante um handle estavel
 * para contar tentativas e, pior, distinguir "criou desafio" de "nao criou" — que e a
 * enumeracao pela porta dos fundos. O `verify` casa por **telefone + codigo**, nao por id.
 *
 * Os dois numeros sao constantes de politica (5 min de validade, 60 s de intervalo minimo),
 * iguais para todo mundo. A UI usa o segundo para o contador do botao "reenviar".
 */
export const RequestOtpResponseSchema = z
  .object({
    status: z.literal('ACCEPTED'),
    /** Validade do codigo, em segundos. 300 (ADR-0009: OTP de 5 min). */
    expiresInSeconds: z.literal(300),
    /** Intervalo minimo ate poder pedir outro. 60 (SECURITY §7.7: `1/min` por telefone). */
    retryAfterSeconds: z.literal(60),
  })
  .strict();
export type RequestOtpResponse = z.infer<typeof RequestOtpResponseSchema>;

/** O corpo literal de `202`. Congelado para que nao exista um segundo lugar que o monte. */
export const OTP_REQUEST_ACCEPTED: RequestOtpResponse = Object.freeze({
  status: 'ACCEPTED',
  expiresInSeconds: 300,
  retryAfterSeconds: 60,
});

// ---------------------------------------------------------------------------
// POST /v1/auth/otp/verify — API §6.20.1
// ---------------------------------------------------------------------------

/**
 * O codigo: **6 digitos, so digitos**. `regex` e nao `number`: `007345` e um codigo valido e
 * `Number('007345')` o transforma em `7345`, que nunca casaria com o hash.
 *
 * O campo se chama `code` porque o contrato publico da API §6.20.1 o chama assim. Dentro do
 * servidor ele muda de nome para `otpCode` na primeira fronteira, porque `code` e tambem o
 * nome do campo do catalogo de erro e a redacao do log nao pode redigir os dois
 * (`platform/logging/redaction.ts`).
 */
export const OtpCodeSchema = z.string().regex(/^\d{6}$/, 'O código tem 6 dígitos numéricos.');
export type OtpCode = z.infer<typeof OtpCodeSchema>;

export const VerifyOtpRequestSchema = z
  .object({
    phone: PhoneBrE164Schema,
    code: OtpCodeSchema,
    /** UUID gerado e persistido pelo cliente; vincula a familia de rotacao a um aparelho. */
    deviceId: z.string().uuid('deviceId deve ser um UUID gerado pelo aplicativo.'),
    /** Rotulo legivel para a tela "meus dispositivos" ("Moto G54 · Chrome"). */
    deviceLabel: z.string().max(80).optional(),
  })
  .strict();
export type VerifyOtpRequest = z.infer<typeof VerifyOtpRequestSchema>;

/**
 * O consentimento que a UI precisa pedir agora — nova versao de politica, por exemplo.
 * Vem vazio no MVP enquanto o modulo de consentimento nao existir; o campo entra desde ja
 * porque a ausencia dele obrigaria uma segunda chamada logo apos o login (API §6.20.1).
 */
export const PendingConsentSchema = z
  .object({
    purpose: ConsentPurposeSchema,
    policyVersion: z.string().min(1),
    blocking: z.boolean(),
  })
  .strict();
export type PendingConsent = z.infer<typeof PendingConsentSchema>;

/**
 * `200 OK`. O que NAO esta aqui e o contrato: sem `accessToken`, sem `refreshToken`, sem
 * `expiresAt` de sessao. O cliente nao gerencia sessao — o cookie faz isso, e a data de
 * expiracao no corpo so serviria para o app tentar renovar sozinho e errar.
 *
 * `firstName` e nulo no primeiro acesso: o cadastro nasce com telefone verificado e mais nada
 * (o nome vem depois, em `PATCH /v1/me/profile`). E `isProfileComplete: false` que leva a UI
 * para o completar-cadastro — nao a ausencia do nome, que seria uma inferencia.
 */
export const VerifyOtpResponseSchema = z
  .object({
    customer: z
      .object({
        id: Uuidv7Schema,
        firstName: z.string().min(1).nullable(),
        isProfileComplete: z.boolean(),
        preferredStoreId: Uuidv7Schema.nullable(),
      })
      .strict(),
    /** Double-submit. Tambem vai no cookie `__Host-csrf`, legivel por JS (SECURITY §7.3). */
    csrfToken: z.string().min(32),
    pendingConsents: z.array(PendingConsentSchema),
  })
  .strict();
export type VerifyOtpResponse = z.infer<typeof VerifyOtpResponseSchema>;

// ---------------------------------------------------------------------------
// POST /v1/auth/refresh e POST /v1/auth/logout
// ---------------------------------------------------------------------------

/**
 * `refresh` **rotaciona** o token opaco (API §6.1). Nao ha corpo de entrada: o que autentica
 * a chamada e o cookie, e um corpo com "qual sessao" seria um convite a IDOR de sessao.
 *
 * A resposta traz o `csrfToken` novo porque a rotacao invalida o par anterior — se o token de
 * CSRF sobrevivesse a rotacao, ele deixaria de estar amarrado a sessao vigente.
 */
export const RefreshSessionResponseSchema = z
  .object({
    csrfToken: z.string().min(32),
  })
  .strict();
export type RefreshSessionResponse = z.infer<typeof RefreshSessionResponseSchema>;

/**
 * `scope` decide entre encerrar **esta** sessao e derrubar **todas** as do sujeito.
 *
 * `ALL` e o que SECURITY §5.2 chama de logout global e o que AMZ-009 exige para o aparelho
 * emprestado: "revogar device B a partir do device A". Default `CURRENT` — o botao "sair" nao
 * pode derrubar o celular do titular por engano.
 */
export const LOGOUT_SCOPES = ['CURRENT', 'ALL'] as const;
export const LogoutScopeSchema = z.enum(LOGOUT_SCOPES);
export type LogoutScope = z.infer<typeof LogoutScopeSchema>;

export const LogoutRequestSchema = z
  .object({
    scope: LogoutScopeSchema.default('CURRENT'),
  })
  .strict();
export type LogoutRequest = z.infer<typeof LogoutRequestSchema>;

/**
 * `200` com a contagem do que caiu. Nao e enfeite: e o que a tela "meus dispositivos" mostra
 * ("3 sessões encerradas") e o que um teste de logout global assere sem ler o banco.
 */
export const LogoutResponseSchema = z
  .object({
    revokedSessions: z.number().int().nonnegative(),
  })
  .strict();
export type LogoutResponse = z.infer<typeof LogoutResponseSchema>;
